/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  Paper,
  Select,
  SelectProps,
  Autocomplete,
  AutocompleteProps,
  InputBaseComponentProps,
  Checkbox,
  ClickAwayListener,
  Popper,
  MenuItem,
  Box,
} from '@mui/material';
import { cloneDeep } from 'lodash';
import withStyles from '@mui/styles/withStyles';
import { colors } from 'spw-theme';
import React, {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import CalendarIcon from 'spw-static/images/calendar-icon.svg';
import styled from 'styled-components';
import Img from 'react-cool-img';
import { FixedSizeList } from 'react-window';
import SearchIcon from 'spw-static/images/search-icon.svg';
import DeleteInputIcon from 'spw-static/images/delete-input-icon.svg';
import BaseTextField from './base-text-field';
import { Item } from '../../types';
import { Row } from './grid';

export const CustomSelect = withStyles({
  root: {
    zIndex: 0,
  },
  select: {
    boxSizing: 'border-box',
    height: '40px',
    fontSize: '14px',
    background: 'white',
    padding: '10px 15px 8px 12px',
    borderRadius: '4px',
    border: `1px solid ${colors.selectBorder}`,
    '&:focus': {
      border: `1px solid ${colors.selectBorder}`,
      background: 'none',
      borderRadius: '4px',
    },
  },
})((props: SelectProps) => {
  return (
    <Select
      variant="outlined"
      IconComponent={KeyboardArrowDown}
      {...props}
      MenuProps={{ disableScrollLock: true }}
    />
  );
});

const CustomPaperComponent = withStyles({
  root: {
    '&.MuiAutocomplete-paper': {
      fontSize: '14px',
      lineHeight: '20px',
      '& .MuiAutocomplete-option': {
        padding: '6px 12px',
      },
    },
  },
})(Paper);

const SearchTextField = styled(BaseTextField)`
  && {
    .MuiInputBase-input::placeholder {
      font-size: 14px;
      font-style: normal;
      line-height: 20px;
      color: ${colors.mainGray} !important;
    }

    .MuiOutlinedInput-root {
      padding: 0;
    }

    .MuiInputBase-root.MuiInputBase-adornedEnd {
      padding: 0 8px 0 0;
      border-radius: 0;
    }

    .MuiAutocomplete-inputRoot .MuiAutocomplete-input:first-child {
      font-family: 'Roboto';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: ${colors.black};
      padding: 10px 12px;
    }
  }
`;

const SearchIconImg = styled(Img)`
  padding-left: 8px;
  margin-right: -1px;

  .MuiSelect-root {
    padding: 10px 12px;
  }
`;

const DeleteInputIconImg = styled(SearchIconImg)`
  cursor: pointer;
`;

type SearchInputProps<T> = {
  className?: string;
  inputValue?: string;
  onChange?: (value: T) => void;
  onDelete: () => void;
  textFieldProps?: ComponentProps<typeof BaseTextField>;
  value: T;
  testingId?: string;
  optionsListFooter?: ReactNode | JSX.Element;
} & Omit<
  AutocompleteProps<T, false, true, boolean>,
  'clearOnEscape' | 'renderInput' | 'onChange' | 'PaperComponent' | 'value'
>;

export function SearchInput<T extends Item | string = string>({
  defaultValue,
  onChange,
  onDelete,
  className,
  textFieldProps,
  value,
  deletable = true,
  testingId,
  optionsListFooter,
  ...props
}: SearchInputProps<T> & { deletable?: boolean }) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>();
  const handleChange = useCallback<
    AutocompleteProps<T, false, true, false>['onChange']
  >(
    (event, selected) => {
      inputRef.current.blur();
      onChange(selected);
    },
    [onChange]
  );

  const filterOptions = useMemo(
    () =>
      createFilterOptions<T>({
        matchFrom: 'any',
      }),
    []
  );

  const PaperWithFooter = useCallback(
    ({
      children,
      className: paperClassName,
    }: {
      children: ReactNode | JSX.Element | JSX.Element[] | ReactNode[];
      className: string;
    }) => (
      <CustomPaperComponent className={paperClassName}>
        {children}
        {optionsListFooter}
      </CustomPaperComponent>
    ),
    [optionsListFooter]
  );
  return (
    <Autocomplete<T, false, true, boolean>
      {...props}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      value={value as NonNullable<T>}
      clearOnEscape={false}
      defaultValue={defaultValue}
      filterOptions={filterOptions}
      onChange={handleChange}
      noOptionsText="No Results"
      open={
        props.inputValue !== undefined
          ? props.inputValue?.length > 1 && isFocused
          : undefined
      }
      PaperComponent={PaperWithFooter}
      renderInput={(params) => {
        const showDeleteIcon =
          params.inputProps['value' as keyof typeof params.inputProps] &&
          deletable;

        const endAdornment = showDeleteIcon ? (
          <DeleteInputIconImg
            src={DeleteInputIcon}
            alt="Delete input icon"
            onClick={onDelete}
          />
        ) : (
          <SearchIconImg src={SearchIcon} alt="Search icon" />
        );

        return (
          <SearchTextField
            {...params}
            {...textFieldProps}
            inputProps={params.inputProps as InputBaseComponentProps}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
            }}
            className={className}
            inputRef={inputRef}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              ...params.InputProps,
              endAdornment,
            }}
            data-testid={testingId}
            variant="outlined"
          />
        );
      }}
    />
  );
}

const CompactMenuItem = styled(MenuItem)`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  && {
    padding: 0 8px;
  }

  & > .MuiCheckbox-root {
    padding: 6px;
  }
`;

const GrayCheckBox = styled(Checkbox)`
  && {
    color: ${colors.fontColor} !important;
  }
`;

const MultiSelectPaper = styled(Paper)`
  border-radius: 0px;
  overflow: auto;
`;

const MultiSelectOptionsHeader = styled(Row)`
  && {
    padding: 8px 16px;
    align-items: center;
    .divider {
      width: 1px;
      height: 16px;
      background-color: ${colors.black};
      margin: 0px 8px;
    }
    .button {
      color: ${colors.mainBlue};
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      cursor: pointer;
      &.disabled {
        color: ${colors.disabledIcons};
      }
    }
  }
`;

export type MultiSelectProps<T> = {
  className?: string;
  inputValue?: string;
  onChange?: (selectedOptions: T[]) => void;
  options: T[];
  onDelete?: () => void;
  renderOption: (option: T) => ReactNode | string;
  textFieldProps?: ComponentProps<typeof BaseTextField>;
  selectedOptions: T[];
  variant: 'dropdown' | 'search';
  filterOptions?: (opt: T, searchText: string) => boolean;
  counterLabel?: string;
  disabled?: boolean;
  visibleItemsCount?: number;
  itemHeight?: number;
};

export function MultiSelect<T extends Item | string = string>({
  onDelete,
  inputValue,
  className,
  options,
  renderOption,
  onChange,
  selectedOptions,
  textFieldProps,
  variant,
  filterOptions,
  counterLabel,
  disabled,
  visibleItemsCount = 4,
  itemHeight = 42,
}: MultiSelectProps<T>) {
  const [isFocused, setIsFocused] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement>(null);

  const getOptionId = useCallback((op: T) => {
    return typeof op === 'string' ? op : op.id;
  }, []);

  const selectedValuesMap = useMemo(() => {
    return selectedOptions.reduce((map, op) => {
      const id = getOptionId(op);
      return { ...map, [id]: true };
    }, {} as { [id: string | number]: boolean });
  }, [selectedOptions, getOptionId]);

  const shouldShowDeleteIcon = !!inputValue;

  const getEndAdornment = () => {
    if (variant === 'search')
      return shouldShowDeleteIcon ? (
        <DeleteInputIconImg
          src={DeleteInputIcon}
          alt="Delete input icon"
          onClick={onDelete}
        />
      ) : (
        <SearchIconImg src={SearchIcon} alt="Search icon" />
      );
    return isFocused ? (
      <KeyboardArrowUp fontSize="small" />
    ) : (
      <KeyboardArrowDown fontSize="small" />
    );
  };

  const endAdornment = getEndAdornment();

  const handleToggleOptionSelected = (option: T) => {
    const optionIndex = selectedOptions.findIndex(
      (op) => getOptionId(op) === getOptionId(option)
    );
    const optionIsAlreadySelected = optionIndex !== -1;
    const newValue = cloneDeep(selectedOptions);
    if (optionIsAlreadySelected) {
      newValue.splice(optionIndex, 1);
    } else {
      newValue.push(option);
    }
    onChange(newValue);
  };

  const filterOptionsDefault = (op: T) => {
    const optionText = renderOption(op);
    if (!inputValue || typeof optionText !== 'string') return true;
    return optionText.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const filteredOptions = options?.filter(
    filterOptions ? (o) => filterOptions(o, inputValue) : filterOptionsDefault
  );

  const selectAll = () => {
    const newSelectedOptionsMap: { [optionId: string]: boolean } = {};
    [...selectedOptions, ...filteredOptions].forEach((op) => {
      newSelectedOptionsMap[getOptionId(op)] = true;
    });

    onChange(options.filter((op) => newSelectedOptionsMap[getOptionId(op)]));
  };

  const clearAll = () => {
    const filteredOptionsMap: { [optionId: string]: boolean } = {};
    filteredOptions.forEach((op) => {
      filteredOptionsMap[getOptionId(op)] = true;
    });
    const newSelectedOptions = selectedOptions.filter((op) => {
      return !filteredOptionsMap[getOptionId(op)];
    });
    onChange(newSelectedOptions);
  };

  const selectAllButtonIsDisabled = useMemo(() => {
    const allFilteredOptionsAreSelected = !filteredOptions.some(
      (op) => !selectedValuesMap[getOptionId(op)]
    );
    return allFilteredOptionsAreSelected;
  }, [filteredOptions, selectedValuesMap, getOptionId]);

  const clearAllButtonIsDisabled = useMemo(() => {
    return !filteredOptions.some((op) => selectedValuesMap[getOptionId(op)]);
  }, [filteredOptions, selectedValuesMap, getOptionId]);

  const textFieldValue = useMemo(() => {
    if (variant === 'search' && (isFocused || !selectedOptions?.length))
      return inputValue;
    return selectAllButtonIsDisabled
      ? `(${options?.length}) All ${counterLabel || textFieldProps?.label}`
      : `(${selectedOptions?.length}) ${
          counterLabel || textFieldProps?.label
        } Selected`;
  }, [
    variant,
    inputValue,
    textFieldProps?.label,
    selectAllButtonIsDisabled,
    options?.length,
    selectedOptions?.length,
    isFocused,
    counterLabel,
  ]);

  useEffect(() => {
    if (!isFocused && onDelete) {
      onDelete();
    }
  }, [isFocused, onDelete]);

  return (
    <ClickAwayListener onClickAway={() => setIsFocused(false)}>
      <div
        role="button"
        tabIndex={-1}
        onClick={(e) => {
          if (!disabled) {
            setAnchorEl(e.currentTarget as HTMLInputElement);
            setIsFocused(true);
          }
        }}
      >
        <SearchTextField
          disabled={disabled}
          className={className}
          InputProps={{
            endAdornment,
          }}
          fullWidth
          style={{
            width: '100%',
            caretColor: variant === 'dropdown' ? 'transparent' : undefined,
          }}
          sx={{
            input: { cursor: 'pointer' },
            svg: { cursor: 'pointer' },
          }}
          variant="outlined"
          focused={isFocused}
          {...textFieldProps}
          value={textFieldValue}
        />
        <Popper
          disablePortal
          open={isFocused}
          anchorEl={anchorEl}
          placement="bottom-start"
          keepMounted={false}
          sx={{ zIndex: 101 }}
        >
          <MultiSelectPaper
            elevation={5}
            style={{ width: anchorEl?.clientWidth }}
          >
            {filteredOptions?.length ? (
              <MultiSelectOptionsHeader>
                <span
                  className={`button ${
                    selectAllButtonIsDisabled ? 'disabled' : ''
                  }`}
                  onClick={selectAllButtonIsDisabled ? undefined : selectAll}
                >
                  Select All
                </span>
                <div className="divider" />
                <span
                  className={`button ${
                    clearAllButtonIsDisabled ? 'disabled' : ''
                  }`}
                  onClick={clearAllButtonIsDisabled ? undefined : clearAll}
                >
                  Clear All
                </span>
              </MultiSelectOptionsHeader>
            ) : (
              <Box padding={2} fontSize={14} color={colors.mainGray}>
                No Results
              </Box>
            )}
            <FixedSizeList
              width="100%"
              height={visibleItemsCount * itemHeight}
              itemSize={itemHeight}
              itemCount={filteredOptions?.length}
              itemData={filteredOptions}
              overscanCount={5}
            >
              {({ index, style, data }) => {
                const op = data[index];
                return (
                  <CompactMenuItem
                    onClick={() => handleToggleOptionSelected(op)}
                    style={style}
                    key={index}
                  >
                    <Row className="option" alignItems="center" width="100%">
                      <GrayCheckBox
                        checked={!!selectedValuesMap[getOptionId(op)]}
                        autoFocus={false}
                      />
                      <Box flex={1}>{renderOption(op)}</Box>
                    </Row>
                  </CompactMenuItem>
                );
              }}
            </FixedSizeList>
          </MultiSelectPaper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
export const YearPickerIcon: SelectProps['IconComponent'] = ({
  className,
  ...props
}) => (
  <Box
    className={className}
    {...props}
    style={{ transform: 'none', marginTop: -2 }}
  >
    <img src={CalendarIcon} alt="calendar" />
  </Box>
);
